export function normalizeRecommendationId(id) {
	if (id == null || id === "") return null;
	const n = Number(id);
	return Number.isFinite(n) ? n : String(id);
}

export function normalizePageUrl(url) {
	if (!url) return "";
	try {
		const u = new URL(url.trim());
		u.hash = "";
		const path = u.pathname.replace(/\/+$/, "") || "/";
		return `${u.origin}${path}${u.search}`;
	} catch {
		return url.trim().replace(/\/+$/, "");
	}
}

export function findPageByUrl(pages, pageUrl) {
	const target = normalizePageUrl(pageUrl);
	return pages?.find((p) => normalizePageUrl(p.url) === target) ?? null;
}

export function recommendationIdsMatch(a, b) {
	return normalizeRecommendationId(a) === normalizeRecommendationId(b);
}

export function isWritingRecommendationSelectable(rec) {
	return rec?.selectable !== false && (rec?.affected_pages?.length ?? 0) > 0;
}

export function findFirstSelectableWritingRecommendation(recommendations) {
	return (recommendations || []).find(isWritingRecommendationSelectable) ?? null;
}

export function findOnlySelectableWritingRecommendation(recommendations) {
	const selectable = (recommendations || []).filter(isWritingRecommendationSelectable);
	return selectable.length === 1 ? selectable[0] : null;
}

const DISPLAY_MD_ESCAPES = {
	n: "\n",
	r: "\r",
	t: "\t",
	s: " ",
	'"': '"',
	"'": "'",
	"\\": "\\",
};

function unescapeDisplayMdOnce(value) {
	return String(value).replace(/\\(.)/g, (_, ch) => {
		if (Object.prototype.hasOwnProperty.call(DISPLAY_MD_ESCAPES, ch)) {
			return DISPLAY_MD_ESCAPES[ch];
		}
		return ch;
	});
}

export function unescapeDisplayMd(md, fallbackText = "") {
	// Only null/undefined should fall back — empty string is intentional (e.g. user cleared edit).
	if (md == null) return fallbackText;

	let result = String(md);
	for (let pass = 0; pass < 5; pass += 1) {
		const next = unescapeDisplayMdOnce(result);
		if (next === result) break;
		result = next;
	}

	// Final pass for any remaining literal API tokens (e.g. partially escaped \\s).
	result = result
		.replace(/\\s/g, " ")
		.replace(/\\n/g, "\n")
		.replace(/\\r/g, "\r")
		.replace(/\\t/g, "\t");

	// API padding often encodes trailing whitespace as \s / newlines with no copy change.
	return result.replace(/[ \t]+$/gm, "").replace(/\n+$/g, "");
}

/** Any of these on any change locks all new deploys until overwrite/discard. */
export const OPEN_DEPLOYMENT_STATUSES = new Set([
	"pending",
	"fetching",
	"backing_up",
	"generating_patch",
	"applying_patch",
	"publishing_temp",
	"verifying",
	"awaiting_review",
]);

export const OPEN_JOB_LOCK_MESSAGE =
	"You already have an open deployment job. Please overwrite or discard it before creating a new one.";

/** Statuses skipped by Deploy all / not eligible to start a new deploy. */
const DEPLOY_SKIPPED_STATUSES = new Set([
	...OPEN_DEPLOYMENT_STATUSES,
	"queued",
	"in_progress",
	"processing",
	"running",
	"overwritten",
	"reverted",
	"completed",
	"success",
	"deployed",
]);

export function getChangeDeploymentStatus(change) {
	if (!change || typeof change !== "object") return "";
	const raw = change.raw && typeof change.raw === "object" ? change.raw : change;
	return String(
		change.deploymentStatus ||
			raw.deployment_status ||
			change.deployment_status ||
			""
	).toLowerCase();
}

export function isOpenDeploymentStatus(status) {
	return OPEN_DEPLOYMENT_STATUSES.has(String(status || "").toLowerCase());
}

/** True when any change on the job is in-progress or awaiting_review. */
export function jobHasOpenDeployment(changes) {
	return (Array.isArray(changes) ? changes : []).some((change) =>
		isOpenDeploymentStatus(getChangeDeploymentStatus(change))
	);
}

/**
 * Eligible for a new auto-deploy (single or as part of Deploy all).
 * Backend includes: never deployed, discarded, failed / failed_patch.
 * Skips: open statuses, overwritten, already deployed, deployable !== true.
 */
export function isChangeEligibleForAutoDeploy(change) {
	if (!change || typeof change !== "object") return false;
	const raw = change.raw && typeof change.raw === "object" ? change.raw : change;
	const deployable = change.deployable ?? raw.deployable;
	if (deployable !== true) return false;

	const allowEdit =
		change.allowEdit !== false &&
		raw.allow_edit !== false &&
		change.allow_edit !== false;
	if (!allowEdit) return false;

	const status = getChangeDeploymentStatus(change);
	if (DEPLOY_SKIPPED_STATUSES.has(status)) return false;
	return true;
}

/** Show Deploy all when the recommendation has ≥1 eligible change. */
export function hasEligibleChangesForDeployAll(changes) {
	const list = Array.isArray(changes) ? changes : [];
	return list.some(isChangeEligibleForAutoDeploy);
}

/** @deprecated Use hasEligibleChangesForDeployAll — backend deploys only eligible changes. */
export function allChangesEligibleForDeployAll(changes) {
	return hasEligibleChangesForDeployAll(changes);
}

export function mapJobChange(change, index, blockCount = null) {
	const isAdded = change.operation === "insert";
	const isOutOfBody =
		isAdded &&
		(change.after_index == null ||
			(blockCount != null && change.after_index >= blockCount));

	const oldContentText =
			change.old_content ||
			unescapeDisplayMd(change.old_content_md, "") ||
			"";

	return {
		id: index,
		type: isAdded ? "added" : "updated",
		tag: change.tag,
		title: change.new_content_text,
		description: change.reasoning,
		reasoning: change.reasoning || "",
		oldContent: oldContentText,
		oldDisplayMd: unescapeDisplayMd(change.old_content_md, change.old_content || ""),
		newContentText: change.new_content_text || "",
		newContentMd: unescapeDisplayMd(
			change.new_content_md ?? null,
			change.new_content_text || ""
		),
		displayMd: unescapeDisplayMd(
			change.new_content_md ?? null,
			change.new_content_text || ""
		),
		allowEdit: change.allow_edit !== false,
		deploymentStatus: change.deployment_status || "",
		deploymentFailureReason: change.deployment_failure_reason || "",
		deploymentJobId:
			change.deployment_job_id ??
			change.deploy_job_id ??
			change.wordpress_job_id ??
			change.job_id ??
			null,
		deployable: change.deployable,
		deployBlockReason: change.deploy_block_reason || "",
		blockIndex: isAdded ? change.after_index : change.index,
		sortIndex: isAdded? (change.after_index ?? Number.MAX_SAFE_INTEGER) + 0.5: (change.index ?? Number.MAX_SAFE_INTEGER),
		isOutOfBody,
		anchorIndex: change.index ?? change.after_index ?? index,
		raw: change,
	};
}

export function hasUpdatedHoverContent(change) {
	return change?.type === "updated" && Boolean(change.oldContent?.trim());
}

export function hasPreviousContentPopover(change) {
	return hasUpdatedHoverContent(change);
}

export function hasChangeHoverTooltip(change) {
	return Boolean(change?.reasoning?.trim()) || hasUpdatedHoverContent(change);
}

export function getChangeTooltipTitle(change) {
	return change?.type === "added" ? "New content added" : "Content updated";
}

export function isLiveStatusFixed(status) {
	const s = String(status || "").toLowerCase();
	return (
		s === "fixed" ||
		s === "verified" ||
		s === "applied" ||
		s === "success" ||
		s === "passed"
	);
}

export function isLiveStatusNotFixed(status) {
	const s = String(status || "").toLowerCase();
	return s === "not_fixed" || s === "not fixed" || s === "failed" || s === "error";
}

export function getValidationChangeResult(latestValidation, changeIndex) {
	if (!latestValidation) return null;
	if (String(latestValidation.status || "").toLowerCase() !== "completed") return null;
	if (!Array.isArray(latestValidation.changes)) return null;

	return (
		latestValidation.changes.find(
			(entry) => entry?.change_index === changeIndex + 1
		) ??
		latestValidation.changes[changeIndex] ??
		null
	);
}

export function getChangeLiveStatus(latestValidation, changeIndex) {
	if (!latestValidation) return null;
	if (String(latestValidation.status || "").toLowerCase() !== "completed") return null;

	const changeResult = getValidationChangeResult(latestValidation, changeIndex);
	const status = changeResult?.status ?? latestValidation.result_status;
	const label =
		changeResult?.status_label ??
		latestValidation.result_status_label ??
		(latestValidation.result_status
			? String(latestValidation.result_status).replace(/_/g, " ")
			: null);
	const explanation = changeResult?.explanation ?? latestValidation.explanation ?? "";

	if (!status && !label) return null;

	return {
		status: status ? String(status).toLowerCase() : "",
		label: label || "Unknown",
		explanation,
	};
}

export function getSkippedRecommendationTitle(skippedItem, debugRecommendations) {
	const rec = debugRecommendations?.find(
		(r) => r.id === skippedItem.recommendation_id
	);
	return rec?.title ?? `Recommendation #${skippedItem.recommendation_id}`;
}

export function truncateUrl(url, maxLen = 24) {
	if (!url || url.length <= maxLen) return url;
	return `${url.slice(0, maxLen)}...`;
}

export function formatWritingAuditOption(audit) {
	const url = audit?.url || audit?.websiteURL || audit?.websiteUrl || "";
	let domain = "";
	try {
		domain = url ? new URL(url).hostname : "";
	} catch {
		domain = url;
	}
	const date = audit?.completed_at
		? new Date(audit.completed_at).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
			})
		: "Unknown date";
	// const count =
	// 	audit?.recommendation_count ??
	// 	audit?.summary?.recommendation_count ??
	// 	"?";
	return `${domain || "Audit"} - ${date} - recommendations`;
}

export function getPageJobId(page) {
	if (!page) return null;
	const id = page.processed_job_id;
	return id != null ? id : null;
}

export function isJobReady(job) {
	return String(job?.status || "").toLowerCase() === "completed";
}
